import { Clear, Print, Search } from "@mui/icons-material";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/system";
import { useEffect, useMemo, useState } from "react";
import useStyles from "./styles";

import dayjs from "dayjs";
import "dayjs/locale/pt-br";
import localizedFormat from "dayjs/plugin/localizedFormat";
import api from "../../../services/apiCliente";

import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import { useNavigate } from "react-router-dom";
import CardDataComponent from "../../../components/overview/cardDataComponent";
import TopBarFilter from "../../../components/topBarFilter";

import { useGridApiRef } from "@mui/x-data-grid";
import generatePdfTable from "../../../utils/generatePdfAutoTable";

import DatagridComponent from "../../../components/datagridComponent";
import GrupoChip from "../../../components/grupoEconomico/GrupoChip";
import useErpsCliente from "../../../components/erp/useErpsCliente";

import TabCPE from "./cpe";
import TabONU from "./onu";

dayjs.extend(localizedFormat);
dayjs.locale("pt-br");

export default function ResumoClient() {
  // Estilização
  const theme = useTheme();
  const styles = useStyles(theme);

  // Funcionamento
  const navigate = useNavigate();
  const [selectedMonth, setSelectedMonth] = useState("actual");
  const [initial_date, setInitial_date] = useState(dayjs().startOf("month"));
  const [final_date, setFinal_date] = useState(dayjs());
  const [snackbar, setSnackbar] = useState(null);
  const [search, setSearch] = useState(null);
  const [serviceClient, setServiceClient] = useState([]);

  const [windowWith, setWindowWith] = useState(window.innerWidth);
  const [openFilter, setOpenFilter] = useState(false);

  const [loading, setLoading] = useState(false);

  const [recurringAlerts, setRecurringAlerts] = useState([]);
  const [loadingReccurringAlerts, setLoadingRecurringAlerts] = useState(false);

  // ERP do cliente logado (grupo econômico com tipos misturados: o padrão da
  // HubSoft, "Serviço").
  const { tipos: tiposErp } = useErpsCliente();
  const erp = tiposErp.includes("HUBSOFT") ? "HUBSOFT" : tiposErp[0] || "";
  const labelErp =
    erp === "HUBSOFT"
      ? "Serviço"
      : erp === "VOALLE"
        ? "Contrato"
        : erp === "IXC"
          ? "Contrato"
          : erp === "VERO"
            ? "Contrato"
            : "Serviço";

  const [newClients, setNewClients] = useState(null);
  const [access, setAccess] = useState(null);
  const [loadingTableCpe, setLoadingTableCpe] = useState(false);
  const [loadingTableOnu, setLoadingTableOnu] = useState(false);
  const [tableCpe, setTableCpe] = useState([]);
  const [tableOnu, setTableOnu] = useState([]);

  // Meses do seletor
  const mesAtual = dayjs().format("MMMM");
  const mesPassado = dayjs().subtract(1, "month").format("MMMM");

  // Busca de alertas
  const [searchAlert, setSearchAlert] = useState("");
  const [filteredAlerts, setFilteredAlerts] = useState([]);
  const [tabValue, setTabValue] = useState(0);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  useEffect(() => {
    const cliente = JSON.parse(localStorage.getItem("userCliente"));
    if (cliente) {
      getAllService(
        cliente?.usuarioCliente?.cliente?.cnpj,
        cliente?.usuarioCliente?.cliente?.clienteErpId,
      );
      getTableCpe();
      getTableOnu();
    }
  }, []);

  useEffect(() => {
    setInitial_date(
      selectedMonth === "actual"
        ? dayjs().startOf("month")
        : dayjs().subtract(1, "month").startOf("month"),
    );

    setFinal_date(
      selectedMonth === "actual"
        ? dayjs()
        : dayjs().subtract(1, "month").endOf("month"),
    );
  }, [selectedMonth]);

  useEffect(() => {
    if (!initial_date) return;
    getRecurringAlerts();
  }, [initial_date]);

  // Recebe dados do grid com ranking de alertas
  const getRecurringAlerts = async () => {
    try {
      setLoadingRecurringAlerts(true);
      const response = await api.post("cliente/recurringAlerts", {
        initial_date: initial_date,
        final_date: final_date,
      });

      setRecurringAlerts(response?.data || []);
    } catch (error) {
      console.error(error);
      setSnackbar({
        children: `Error:${
          error?.response?.data?.error ||
          "Não foi possível obter alertas recorrentes"
        } `,
        severity: "error",
      });
    } finally {
      setLoadingRecurringAlerts(false);
    }
  };
  const getAllService = async (cnpj, clienteErpId) => {
    try {
      setLoading(true);
      const response = await api.post("/cliente/find/allServiceClient", {
        cnpj: cnpj,
        idClienteErp: clienteErpId,
      });
      setServiceClient(response?.data || []);
    } catch (error) {
      console.error(error);
      setSnackbar({
        children: `Error:${
          error?.response?.data?.error || "Não foi possível obter serviços"
        } `,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  /////////////////////////////////////
  const getTableCpe = async (signal) => {
    try {
      setLoadingTableCpe(true);
      const response = await api.get("/cliente/findMany/resumeCpe", {
        signal: signal,
      });

      setTableCpe(response?.data || null);
    } catch (error) {
      console.error("Erro na requisição CPE:", error);
    } finally {
      setLoadingTableCpe(false);
    }
  };
  const getTableOnu = async (signal) => {
    try {
      setLoadingTableOnu(true);
      const response = await api.get("/cliente/findMany/resumeOnu", {
        signal: signal,
      });
      setTableOnu(response?.data || null);
    } catch (error) {
      console.error("Erro na requisição ONU:", error);
    } finally {
      setLoadingTableOnu(false);
    }
  };

  // Nome do serviço por dono + id: num grupo econômico com membros de ERPs
  // diferentes o mesmo id de serviço pode existir em dois membros.
  const chaveServico = (row) =>
    `${row?.ownerClienteId || ""}|${row?.servicoId ?? row?.id_cliente_servico}`;
  const serviceMap = useMemo(() => {
    const map = {};
    const allServices = serviceClient || [];

    allServices.forEach((servico) => {
      map[chaveServico(servico)] =
        `${servico.id_cliente_servico} - ${servico.referencia ? servico.referencia + " - " : ""}${servico.nome}`;
    });

    return map;
  }, [serviceClient]);

  const enrichedTableCpe = useMemo(() => {
    if (!tableCpe) return [];
    return tableCpe.map((item) => ({
      ...item,
      nomeServico: serviceMap[chaveServico(item)] || "Serviço não encontrado",
    }));
  }, [tableCpe, serviceMap]);

  const enrichedTableOnu = useMemo(() => {
    if (!tableOnu) return [];
    return tableOnu.map((item) => ({
      ...item,
      nomeServico: serviceMap[chaveServico(item)] || "Serviço não encontrado",
    }));
  }, [tableOnu, serviceMap]);
  /////////////////////////////////////

  // C A R D S LÓGICA DINÂMICA (Depende da Tab Ativa)
  const activeTableData = tabValue === 0 ? tableCpe : tableOnu;
  const activeTableLoading = tabValue === 0 ? loadingTableCpe : loadingTableOnu;

  const cardParams = [
    {
      key: "up",
      name: "Up",
      typeIcon: 4,
      color: "statusUp",
      loading: activeTableLoading,
    },
    {
      key: "down",
      name: "Down",
      typeIcon: 4,
      color: "statusDown",
      loading: activeTableLoading,
    },
    {
      key: "alerts",
      name: "Alertas",
      typeIcon: 7,
      loading: loadingReccurringAlerts,
    },
  ];

  const cardData = {
    up:
      tabValue !== 0
        ? activeTableData?.filter((row) => {
            const p = parseFloat(row?.receivedPower);
            return !isNaN(p) && p > -40 && p < 0;
          })?.length || 0
        : activeTableData?.filter((row) => row?.availability === "1")?.length ||
          0,
    down:
      tabValue !== 0
        ? activeTableData?.filter((row) => {
            const p = parseFloat(row?.receivedPower);
            return isNaN(p) || p <= -40 || p >= 0;
          })?.length || 0
        : activeTableData?.filter(
            (row) => row?.availability === "0" || !row?.availability,
          )?.length || 0,
    alerts: Array.isArray(recurringAlerts) ? recurringAlerts?.length : 0,
    uniqueAccess: access?.[selectedMonth]
      ? `${access?.[selectedMonth]} %`
      : "Não encontrado",
    newClient: newClients?.[selectedMonth] || "Não encontrado",
  };

  // G R I D
  const [filteredRows, setFilteredRows] = useState([]);
  const [filteredRowsOnu, setFilteredRowsOnu] = useState([]);
  // Filtra os dados a serem exibidor de acordo com o campo buscar
  useEffect(() => {
    if (search) {
      setFilteredRows(
        enrichedTableCpe?.filter((row) => {
          const res = row?.percentile?.find((th) =>
            dayjs(initial_date)?.isSame(dayjs(th?.refferenceMonth), "month"),
          );
          return (
            row?.nomeServico?.toLowerCase()?.includes(search?.toLowerCase()) ||
            row?.ownerNome?.toLowerCase()?.includes(search?.toLowerCase()) ||
            row?.host?.toLowerCase()?.includes(search?.toLowerCase()) ||
            formatUptime(row?.uptime)
              ?.toLowerCase()
              ?.includes(search?.toLowerCase()) ||
            (row?.availability === "1" &&
              "up"?.includes(search?.toLowerCase())) ||
            (row?.availability === "0" &&
              "down"?.includes(search?.toLowerCase())) ||
            (!!res ? formatBytes(res?.percentilConsumption) : "")
              ?.toLowerCase()
              ?.includes(search?.toLowerCase())
          );
        }),
      );
    } else {
      setFilteredRows(enrichedTableCpe || []);
    }
  }, [search, enrichedTableCpe, initial_date, formatBytes]);
  useEffect(() => {
    if (search) {
      setFilteredRowsOnu(
        enrichedTableOnu?.filter((row) => {
          const res = row?.percentile?.find((th) =>
            dayjs(initial_date)?.isSame(dayjs(th?.refferenceMonth), "month"),
          );

          return (
            row?.onuItem?.toLowerCase()?.includes(search?.toLowerCase()) ||
            row?.transmittedPower
              ?.toLowerCase()
              ?.includes(search?.toLowerCase()) ||
            row?.nomeServico?.toLowerCase()?.includes(search?.toLowerCase()) ||
            row?.ownerNome?.toLowerCase()?.includes(search?.toLowerCase()) ||
            row?.host?.toLowerCase()?.includes(search?.toLowerCase()) ||
            (row?.availability === "1" &&
              "up"?.includes(search?.toLowerCase())) ||
            (row?.availability === "0" &&
              "down"?.includes(search?.toLowerCase())) ||
            (!!res ? formatBytes(res?.percentilConsumption) : "")
              ?.toLowerCase()
              ?.includes(search?.toLowerCase())
          );
        }),
      );
    } else {
      setFilteredRowsOnu(enrichedTableOnu || []);
    }
  }, [search, enrichedTableOnu, initial_date, formatBytes]);

  const formatUptime = (totalSeconds) => {
    if (!totalSeconds) return "00:00:00:00";

    const seconds = parseInt(totalSeconds, 10);

    if (isNaN(seconds)) return totalSeconds;

    if (seconds <= 0) return "00:00:00:00";

    const dias = Math.floor(seconds / (3600 * 24));
    const horas = Math.floor((seconds % (3600 * 24)) / 3600);
    const minutos = Math.floor((seconds % 3600) / 60);
    const seg = Math.floor(seconds % 60);

    const pad = (num) => String(num).padStart(2, "0");

    return `${pad(dias)}d:${pad(horas)}h:${pad(minutos)}m:${pad(seg)}s`;
  };

  // Grupo econômico: exibe a empresa membro dona de cada registro
  const temEmpresaGrupoAlertas = recurringAlerts?.some(
    (row) => !!row?.ownerNome,
  );

  const recurringAlertsColumns = [
    {
      editable: false,
      field: "id",
      headerName: "ID",
      hide: true,
    },
    {
      field: "servicoId",
      headerName: labelErp,
      flex: 1,
      renderCell: (params) =>
        serviceMap[chaveServico(params?.row)] || "Serviço não encontrado",
    },
    // Coluna exibida apenas para usuários de grupo econômico (IXC)
    ...(temEmpresaGrupoAlertas
      ? [
          {
            field: "ownerNome",
            headerName: "Empresa",
            flex: 0.8,
            renderCell: (params) =>
              params?.row?.ownerNome ? (
                <GrupoChip variant="owner" label={params?.row?.ownerNome} />
              ) : (
                ""
              ),
          },
        ]
      : []),
    { field: "interface", headerName: "Interface", flex: 0.8 },
    {
      field: "description",
      headerName: "Descrição",
      flex: 1,
      valueFormatter: (params) => params?.replaceAll('"', ""),
    },
    { field: "occurrences", headerName: "Ocorrências", flex: 0.4 },
  ];

  const actions = [
    {
      icon: <FilterAltOutlinedIcon />,
      name: "Filtro",
      onClick: () => setOpenFilter(true),
    },
  ];
  const selectorComponentLeft = (
    <TextField
      variant="outlined"
      label="Buscar"
      placeholder={`${labelErp}, IP, Serial, Etc...`}
      value={search || ""}
      size="small"
      sx={styles.textFieldSearch}
      onChange={(event) => {
        setSearch(event.target.value);
      }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <Search />
          </InputAdornment>
        ),
        endAdornment: search ? (
          <InputAdornment position="end">
            <IconButton onClick={() => setSearch("")} edge="end">
              <Clear />
            </IconButton>
          </InputAdornment>
        ) : null,
      }}
    />
  );

  const selectorComponentRight = (
    <FormControl sx={styles.formControl} size="small">
      <Select
        labelId="demo-customized-select-label"
        id="demo-customized-select"
        value={selectedMonth || ""}
        onChange={(e) => {
          setSelectedMonth(e?.target?.value);
        }}
      >
        <MenuItem value={"actual"}>
          {mesAtual[0]?.toUpperCase() + mesAtual?.substring(1)}
        </MenuItem>
        <MenuItem value={"last"}>
          {mesPassado[0]?.toUpperCase() + mesPassado?.substring(1)}
        </MenuItem>
      </Select>
    </FormControl>
  );

  // Filtrar os alertas
  useEffect(() => {
    if (searchAlert) {
      setFilteredAlerts(
        recurringAlerts?.filter((alert) => {
          return (
            alert?.nome
              ?.toLowerCase()
              ?.includes(searchAlert?.toLowerCase()) ||
            alert?.host?.toLowerCase()?.includes(searchAlert?.toLowerCase()) ||
            alert?.ownerNome
              ?.toLowerCase()
              ?.includes(searchAlert?.toLowerCase()) ||
            alert?.interface
              ?.toLowerCase()
              ?.includes(searchAlert?.toLowerCase()) ||
            alert?.description
              ?.toLowerCase()
              ?.includes(searchAlert?.toLowerCase()) ||
            alert?.occurrences?.toString()?.includes(searchAlert)
          );
        }),
      );
    } else {
      setFilteredAlerts(recurringAlerts);
    }
  }, [searchAlert, recurringAlerts]);

  {
    /** Gerar PDF de alertas recorrentes com autoTable */
  }
  const [loadingPdf, setLoadingPdf] = useState(false);
  const apiRefAlerts = useGridApiRef();

  async function gerarPdfAutoTable() {
    // PEGAR AS LINHAS EXATAMENTE COMO ESTÃO NO DATAGRID
    const sortedIds = apiRefAlerts.current.getSortedRowIds();
    const orderedRowsRaw = sortedIds.map(
      (id) => apiRefAlerts.current.getRow(id), // Obtem as linhas do grid
    );

    // MONTAR AS LINHAS DO PDF COM BASE NA ORDEM DO GRID
    const rows = orderedRowsRaw?.map((alert) => [
      serviceMap[chaveServico(alert)] || "Serviço não encontrado",
      ...(temEmpresaGrupoAlertas ? [alert?.ownerNome || ""] : []),
      alert?.interface || "",
      alert?.description?.replaceAll('"', "") || "",
      alert?.occurrences?.toString() || "0",
    ]);

    const headers = [
      [
        labelErp,
        ...(temEmpresaGrupoAlertas ? ["Empresa"] : []),
        "Interface",
        "Descrição",
        "Ocorrências",
      ],
    ];

    const title = "alertas - recorrentes.pdf";

    try {
      await generatePdfTable({
        setLoadingPdf,
        api,
        rows,
        headers,
        theme,
        title,
      });
    } catch (error) {
      setSnackbar({
        children: `Error:${error.message || "Erro ao gerar PDF"} `,
        severity: "error",
      });
    }
  }

  return (
    <Box sx={styles.container}>
      {/**F I L T R O S */}
      <TopBarFilter
        actions={actions}
        selectorComponentLeft={selectorComponentLeft}
        selectorComponentRight={selectorComponentRight}
        windowWidth={windowWith}
        openFilter={openFilter}
        setOpenFilter={setOpenFilter}
        type={1}
      />
      <Box sx={styles.content}>
        {/**C A R D S */}
        <Grid container spacing={1} columns={12}>
          {cardParams?.map((card) => (
            <Grid item xs={12} sm={6} md={4} lg={4} xl={4} key={card?.name}>
              <CardDataComponent
                cardTile={card?.name}
                cardValue={cardData?.[card?.key] || 0}
                typeIcon={card?.typeIcon}
                colorCard={card?.color}
                loading={card?.loading}
              />
            </Grid>
          ))}
        </Grid>

        {/**M A I N   C O N T E N T */}
        <Grid container spacing={1}>
          {/**C L I E N T E S */}
          <Grid item xs={12}>
            <Box
              sx={{
                bgcolor: theme.palette.background.paper,
                p: 1,
                borderRadius: "12px",
              }}
            >
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                sx={styles.tabs}
              >
                <Tab
                  sx={styles.tab}
                  label={
                    <Box sx={styles.boxTabs}>
                      <Typography>CPE</Typography>
                      <Chip
                        label={
                          loadingTableCpe ? (
                            <CircularProgress size={14} color="inherit" />
                          ) : (
                            tableCpe?.length || 0
                          )
                        }
                        size="small"
                        sx={styles.stylesChips}
                      />
                    </Box>
                  }
                />
                <Tab
                  label={
                    <Box sx={styles.boxTabs}>
                      <Typography>ONU</Typography>
                      <Chip
                        label={
                          loadingTableOnu ? (
                            <CircularProgress size={14} color="inherit" />
                          ) : (
                            tableOnu?.length || 0
                          )
                        }
                        size="small"
                        sx={styles.stylesChips}
                      />
                    </Box>
                  }
                  sx={styles.tab}
                />
              </Tabs>
              <Box
                sx={{
                  bgcolor: theme.palette.background.wallpaper,
                  p: 2,
                  borderRadius: "12px",
                  borderTopLeftRadius: "0px",
                  mt: "-6px",
                }}
              >
                {tabValue === 0 && (
                  <TabCPE
                    tableCpe={enrichedTableCpe}
                    search={search}
                    initial_date={initial_date}
                    final_date={final_date}
                    theme={theme}
                    styles={styles}
                    formatUptime={formatUptime}
                    formatBytes={formatBytes}
                    navigate={navigate}
                    labelErp={labelErp}
                    filteredRows={filteredRows}
                    loading={loadingTableCpe}
                  />
                )}

                {tabValue === 1 && (
                  <TabONU
                    tableOnu={enrichedTableOnu}
                    search={search}
                    theme={theme}
                    styles={styles}
                    formatUptime={formatUptime}
                    navigate={navigate}
                    initial_date={initial_date}
                    final_date={final_date}
                    labelErp={labelErp}
                    filteredRows={filteredRowsOnu}
                    loading={loadingTableOnu}
                  />
                )}
              </Box>
            </Box>
          </Grid>

          {/**A L E R T A S    R E C O R R E N T E S */}
          <Grid item xs={12}>
            <Paper
              sx={styles.paperRecurringAlerts}
              elevation={theme.palette.mode == "dark" ? 0 : 3}
            >
              {loadingReccurringAlerts && (
                <LinearProgress sx={styles.linearProgress} />
              )}
              <Box sx={styles.containerHeaderRecurringAlerts}>
                <Typography variant="h6" sx={styles.gridTitle}>
                  Alertas - Recorrentes
                </Typography>
                <Box sx={styles.boxRecurringAlertsSearchePrintFields}>
                  {/** Buscar por alertas */}
                  <TextField
                    variant="outlined"
                    label="Buscar alertas"
                    placeholder="Serviço, Interface, Descrição..."
                    size="small"
                    sx={styles.recurringAlertSearchField}
                    value={searchAlert || ""}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search />
                        </InputAdornment>
                      ),
                      endAdornment: searchAlert ? (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setSearchAlert(null)}
                            edge="end"
                            size="small"
                          >
                            <Clear />
                          </IconButton>
                        </InputAdornment>
                      ) : null,
                    }}
                    onChange={(event) => setSearchAlert(event.target.value)}
                    disabled={
                      !filteredAlerts?.length ||
                      !!loadingPdf ||
                      !!loadingReccurringAlerts
                    }
                  />
                  <IconButton
                    size="small"
                    onClick={() => {
                      gerarPdfAutoTable();
                    }}
                    disabled={
                      !filteredAlerts?.length ||
                      !!loadingPdf ||
                      !!loadingReccurringAlerts
                    }
                  >
                    <Print
                      sx={
                        !filteredAlerts?.length ||
                        loadingPdf ||
                        loadingReccurringAlerts
                          ? { color: theme.palette.colors.gray.light }
                          : styles.recurringAlertPrintIcon
                      }
                    />
                  </IconButton>
                </Box>
              </Box>
              <Divider />
              <Box sx={styles.containerDataGrid}>
                <DatagridComponent
                  loading={loadingReccurringAlerts}
                  columns={recurringAlertsColumns}
                  rows={filteredAlerts || []}
                  isPagination={false}
                  apiRef={apiRefAlerts}
                />
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      {!!snackbar && (
        <Snackbar
          open
          onClose={() => setSnackbar(null)}
          autoHideDuration={2000}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert {...snackbar} onClose={() => setSnackbar(null)} />
        </Snackbar>
      )}
    </Box>
  );
}

const formatBytes = (bytes) => {
  if (bytes == 0) return "0 B";
  const k = 1000;
  const sizes = ["bps", "Kbps", "Mbps", "Gbps", "Tbps", "Pbps", "Ebps"];

  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};
